import React, { useMemo } from 'react';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, ReferenceLine, Tooltip } from 'recharts';
import { ArrowUp, ArrowDown } from 'lucide-react';
import MetricCard from './MetricCard';
import StatDisplay from './StatDisplay';
import { getPercentChange, axisStyle } from './marketUtils';


const YieldsCard = ({ data: { tnx, fvx, irx } }) => {
  const chartData = useMemo(() => {
    if (!tnx || !irx) return [];
    return tnx.map((point, i) => {
      const tenYear = point.close;
      const twoYear = irx[i]?.close || 0;
      const isInverted = twoYear > tenYear;
      return {
        timestamp: point.timestamp,
        value: tenYear - twoYear,
        isInverted,
      };
    });
  }, [tnx, irx]);

  const latestData = {
    tenYear: tnx[tnx.length - 1]?.close || 0,
    twoYear: irx[irx.length - 1]?.close || 0,
    fiveYear: fvx[fvx.length - 1]?.close || 0,
  };

  const weekPriorData = {
    tenYear: tnx[tnx.length - 6]?.close || 0,
    twoYear: irx[irx.length - 6]?.close || 0,
    fiveYear: fvx[fvx.length - 6]?.close || 0,
  }

  const changes = {
    twoYear: getPercentChange(latestData.twoYear, weekPriorData.twoYear),
    fiveYear: getPercentChange(latestData.fiveYear, weekPriorData.fiveYear),
    tenYear: getPercentChange(latestData.tenYear, weekPriorData.tenYear),
  };



  const isCurrentlyInverted = latestData.twoYear > latestData.tenYear;
  const formatPercent = (value) => `${value.toFixed(2)}%`;

  const daysInCurrentState = useMemo(() => {
    if (chartData.length === 0) return 0;

    const currentState = isCurrentlyInverted;
    let streakDays = 0;

    // Start from the most recent day and count backwards until we find a different state
    for (let i = chartData.length - 1; i >= 0; i--) {
      if (chartData[i].isInverted === currentState) {
        streakDays++;
      } else {
        break;
      }
    }

    return streakDays;
  }, [chartData, isCurrentlyInverted]);

  const headerRight = (
    <div className={`flex items-center gap-2 py-0.5 px-2 rounded-full ${isCurrentlyInverted ? 'text-rose-600 border border-rose-800' : 'text-emerald-800 border border-emerald-600'} font-medium`}>
      {isCurrentlyInverted ? 'Inverted' : 'Normal'}
      {isCurrentlyInverted ?
        <ArrowDown className="w-5 h-5" /> :
        <ArrowUp className="w-5 h-5" />
      }
    </div>
  );

  const statsContent = (
    <div className="grid grid-cols-3 gap-4">
      <div className={'p-2'}>
        <StatDisplay
          title="2Y Yield"
          value={formatPercent(latestData.twoYear)}
          change={changes.twoYear}
          period="7d"
        />
      </div>
      <div className={'p-2'}>
        <StatDisplay
          title="5Y Yield"
          value={formatPercent(latestData.fiveYear)}
          change={changes.fiveYear}
          period="7d"
        />
      </div>
      <div className={'p-2'}>
        <StatDisplay
          title="10Y Yield"
          value={formatPercent(latestData.tenYear)}
          change={changes.tenYear}
          period="7d"
        />
      </div>
    </div>
  );


  const chartContent = (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} barSize={8}>
        <YAxis
          {...axisStyle}
          domain={['auto', 'auto']}
        />
        <XAxis
          {...axisStyle}
          dataKey="timestamp"
          height={20}
        />
        <ReferenceLine y={0} stroke="#e2e8f0" strokeWidth={2} />
        <Tooltip />
        <Bar
          dataKey="value"
          fill={(data) => data.isInverted ? '#ef4444' : '#22c55e'}
          radius={[4, 4, 4, 4]}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  );


  const bottomContent = (
    <div className="grid grid-cols-3 gap-4 px-2">
      <div>
        <div className="text-lg card-value text-gray-900">
          {formatPercent(Math.abs(latestData.tenYear - latestData.twoYear))}
        </div>
        <div className="text-sm text-gray-500 mt-1 card-label">Current Spread</div>
      </div>
      <div>
        <div className="text-lg card-value text-gray-900">
          {formatPercent(latestData.tenYear)}
        </div>
        <div className="text-sm text-gray-500 mt-1 card-label">10Y Yield</div>
      </div>
      <div>
        <div className={`text-lg card-value ${isCurrentlyInverted ? 'text-red-500' : 'text-green-500'}`}>
          {daysInCurrentState} days
        </div>
        <div className="text-sm text-gray-500 mt-1 card-label">
          {isCurrentlyInverted ? 'Since Inversion' : 'Since Normal'}
        </div>
      </div>
    </div>
  );

  return (
    <MetricCard
      title="Treasury Yields"
      headerRight={headerRight}
      statsContent={statsContent}
      chartContent={chartContent}
      bottomContent={bottomContent}
    />
  );
};

export default YieldsCard;